import {useEffect, useRef, useMemo, useState} from 'react'
import type {ReactNode} from 'react'
import {clsx} from 'clsx'
import {hpcAttrs} from '@github-ui/hpc'
import {IconButton} from '@primer/react'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {Filter} from '@github-ui/filter'
import {XCircleFillIcon} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {TaskListLayout} from '../task-list/TaskListLayout'
import {TaskListItem} from '../task-list/TaskListItem'
import {TaskDescription} from '../task-list/TaskDescription'
import {TaskActionProviders} from '../task-list/TaskActionProviders'
import {TaskRelativeTime} from '../task-list/TaskRelativeTime'
import {ArchiveErrorBanner} from '../ArchiveErrorBanner'
import {FilterActionBar} from '../filters/FilterActionBar'
import {NEEDS_ATTENTION_QUERY} from './queries'
import {useTasks} from '../../hooks/useTasks'
import {useHiddenEarlyTaskIds} from '../../ahp/early-task-visibility'
import {useTaskFilters} from '../../hooks/use-task-filters'
import {useTaskFilterParams} from '../../hooks/use-task-filter-params'
import {useFilterableAgents, withBuiltinIdeAgentFallbacks} from '../../hooks/use-filterable-agents'
import {ALL_REPOSITORIES_FILTER, NO_REPO_FILTER} from '../../hooks/use-repo-tasks-filter'
import {getReadableSessionState} from '../../types/session'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import type {ActiveTab} from '../../types/filter-types'
import {CLOUD_TYPE_ENVIRONMENT_KINDS, getTypeOptionsForAgents} from '../../utils/filter-utils'
import {sortTasksByDate, transformAPITasksToUnhydrated} from '../../utils/task-helpers'
import {readFiltersFromSearchParams} from '../../utils/session-filter-url'
import {formatSessionCount} from '../../utils/session-count'
import {generateTaskUrlForRepo, shouldUseGlobalTaskUrls, type TaskUrlScope} from '../../utils/generate-task-path'
import type {UITask} from '../../types/task'
import type {APITask} from '../../types/api-task'
import type {IdToPullRequestMap} from '../../types/pull'
import {useSearchParams} from '@github-ui/react-router'
import {useCurrentUserOptional} from '../../contexts/CurrentUserContext'
import {parsePageNumber} from '../../utils/url-helpers'
import {TASK_TYPE_CHAT} from '../../utils/api-url-helpers'
import styles from './AgentsTaskList.module.css'

const PAGE_SIZE = 10
const TASK_LIST_SCOPE = 'agents-task-list'
const CHAT_LIST_SCOPE = 'agents-task-list-chats'

export interface AgentsTaskListProps {
  repoFilter?: string
  repoId?: number
  setRepoFilter?: (repo: {nameWithOwner: string; id?: number} | undefined) => void
  /**
   * True on the Needs Attention view. Drives view-level behavior (hidden Active/Completed
   * tabs, session count, and the default filter query that preserves `status:needs_attention`
   * across resets) and, downstream, suppresses the per-row attention styling.
   */
  isNeedsAttentionView?: boolean
  /** Server-rendered first page of tasks (raw APITask shape) used to seed the SSR list. */
  initialTasks?: APITask[]
  /** SSR-seeded PR hydration for the initial tasks, keyed by resource id. */
  initialPullRequestsMap?: IdToPullRequestMap
  /** Reports the unfiltered total task count for the current view. */
  onTotalTaskCount?: (count: number | undefined, hasActiveFilters: boolean) => void
  trackHpc?: boolean
  /**
   * Controls which task URL policy this list owns. The shared default remains
   * repository-scoped for legacy consumers.
   */
  taskUrlScope?: TaskUrlScope
}

function getSessionStateText(task: UITask): string {
  const prState = task.pull?.state ?? 'open'
  const prInMergeQueue = task.pull?.inMergeQueue ?? false

  if (prState === 'merged') return 'Merged'
  if (prState === 'closed') return 'Closed'
  if (prInMergeQueue) return 'Queued to merge'
  return getReadableSessionState(task.latest_session_state)
}

export function AgentsTaskList({
  repoFilter,
  repoId,
  setRepoFilter,
  isNeedsAttentionView = false,
  initialTasks,
  initialPullRequestsMap,
  onTotalTaskCount,
  trackHpc = false,
  taskUrlScope = 'repository',
}: AgentsTaskListProps) {
  const [searchParams] = useSearchParams()
  const currentUser = useCurrentUserOptional()
  const currentUserLogin = currentUser?.currentUser?.login
  const currentUserId = currentUser?.currentUser?.id
  const currentPage = parsePageNumber(searchParams.get('page'))

  // Determine if we're in repo-filtered mode (advanced filters available)
  const isRepoScoped = Boolean(repoFilter && repoFilter !== NO_REPO_FILTER && repoFilter !== ALL_REPOSITORIES_FILTER)
  const repoNwo = isRepoScoped ? repoFilter : undefined
  const [repoOwner, repoName] = repoNwo?.split('/', 2) ?? []

  const useGlobalTaskUrls = shouldUseGlobalTaskUrls({
    taskUrlScope,
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
    repositoryTaskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_repository_task_detail'),
  })
  const getTaskUrl = useGlobalTaskUrls
    ? (task: UITask, _nwo: string | undefined, taskSearchParams: URLSearchParams) =>
        generateTaskUrlForRepo({
          taskId: task.id,
          sessionId: task.latest_session_id,
          searchParams: taskSearchParams,
        })
    : undefined

  // User-scoped list of agents the current user can filter by. Drives both the
  // Type filter options (derived by agent group) and the Type→agentId resolution.
  const {data: rawAgents = []} = useFilterableAgents({enabled: true})

  const agents: AgentInfo[] = useMemo(() => withBuiltinIdeAgentFallbacks(rawAgents), [rawAgents])

  const typeOptions = useMemo(() => getTypeOptionsForAgents(agents), [agents])

  // The active tab (Active/Completed) is driven by the URL `is:` qualifier so it
  // stays in sync with the filter bar (typing `is:open`/`is:closed` also works).
  const urlFilters = useMemo(
    () => readFiltersFromSearchParams(searchParams, currentUserLogin),
    [searchParams, currentUserLogin],
  )
  // Default to the Active tab when no `is:` qualifier is present; otherwise honor the URL value.
  const filterActiveTab: ActiveTab = urlFilters.status === 'closed' ? 'closed' : 'open'

  // Task filter state from URL params (manages status, agent, sort, search)
  const {
    filterValue,
    filterProviders,
    isNonDefault,
    handleFilterChange,
    handleFilterSubmit,
    handleResetToDefault,
    handleTabClick,
    sortDirection,
    handleSortChange,
    selectedStatuses,
    handleStatusChange,
    agentOptions,
    selectedAgents,
    handleAgentChange,
    selectedTypes,
    handleTypeChange,
    hasActiveFilters,
    selectedCreators,
    searchQuery: filterSearchQuery,
  } = useTaskFilters({
    activeTab: filterActiveTab,
    currentUserLogin,
    apiAgents: agents,
    hideAuthorFilter: true,
    useQSyntax: true,
    omitAuthorProvider: true,
    // On the Needs Attention view, keep `status:needs_attention` on reset (e.g. repo
    // change) so switching repos doesn't bounce back to the My sessions view.
    defaultQuery: isNeedsAttentionView ? NEEDS_ATTENTION_QUERY : undefined,
  })

  // Resolve URL filter params to API-ready values (agent IDs, etc.)
  const {
    customAgentName,
    statuses,
    sort,
    searchQuery,
    agentIds: resolvedAgentIds,
    creatorIds,
    filtersReady,
  } = useTaskFilterParams({
    filtersEnabled: true,
    owner: repoOwner ?? '',
    repo: repoName ?? '',
    selectedCreators,
    selectedAgents,
    selectedStatuses,
    sortDirection,
    searchQuery: filterSearchQuery,
    agents,
    currentUserId: currentUserId ?? undefined,
    currentUserLogin: currentUserLogin ?? undefined,
    isFilteredByCurrentUser: true,
  })

  // Resolve type filter (cli/vscode/cloud) to agent IDs
  const typeAgentIds = useMemo(() => {
    if (selectedTypes.length === 0) return undefined
    return agents.flatMap(a => (selectedTypes.includes(a.group ?? 'cloud') && a.id != null ? [Number(a.id)] : []))
  }, [selectedTypes, agents])

  // Merge agent IDs from agent filter and type filter
  const agentIds = useMemo(() => {
    if (!resolvedAgentIds && !typeAgentIds) return undefined
    const merged = [...(resolvedAgentIds ?? []), ...(typeAgentIds ?? [])]
    return merged.length > 0 ? merged : undefined
  }, [resolvedAgentIds, typeAgentIds])

  // Sandbox/codespace sessions are backed by an environment kind rather than a
  // cloud agent ID, so resolving `Type: Cloud agents` to agent IDs alone drops
  // them. Request those environment kinds alongside the cloud agent IDs so they
  // are included under the cloud-agent filter (parity with the global menu).
  const includeEnvironmentKinds = useMemo(
    () => (selectedTypes.includes('cloud') ? CLOUD_TYPE_ENVIRONMENT_KINDS : undefined),
    [selectedTypes],
  )

  // Reset all advanced filters when repo changes
  const prevRepoRef = useRef(repoFilter)
  useEffect(() => {
    if (prevRepoRef.current !== repoFilter) {
      handleResetToDefault()
    }
    prevRepoRef.current = repoFilter
  }, [repoFilter, handleResetToDefault])

  // Seed SSR data while the current view still reflects the server-rendered URL.
  // Agent/type filters resolve asynchronously (slug -> ID), so exclude them via
  // synchronous URL intent (selectedAgents/selectedTypes). Synchronous filters
  // (status, sort, search, tab) are allowed but pinned to the URL present at mount,
  // so a later client-side filter change never reseeds under the wrong query key.
  const syncFilterSignature = JSON.stringify({
    tab: filterActiveTab,
    statuses: statuses ?? null,
    sort: sort ?? null,
    searchQuery: searchQuery ?? null,
  })
  const [initialSyncFilterSignature] = useState(syncFilterSignature)
  const isSeedableView =
    currentPage === 1 &&
    !repoFilter &&
    selectedAgents.length === 0 &&
    selectedTypes.length === 0 &&
    syncFilterSignature === initialSyncFilterSignature
  const seededInitialTasks = isSeedableView && initialTasks ? transformAPITasksToUnhydrated(initialTasks) : undefined
  const seededPullRequestsMap = isSeedableView ? initialPullRequestsMap : undefined

  // Some API deployments return chat tasks only from the dedicated type=chat
  // query, while others also include them in the general task query. Fetch both
  // streams for compatibility, then identify chats by their presence in the
  // dedicated response before deduplicating and paginating the merged result.
  const includeChats = !repoFilter || repoFilter === NO_REPO_FILTER
  const cumulativePageSize = currentPage * PAGE_SIZE
  const taskQuery = useTasks({
    taskFilter: filterActiveTab === 'closed' ? 'completed' : 'active',
    includeCounts: true,
    currentPage: includeChats ? 1 : currentPage,
    pageSize: includeChats ? cumulativePageSize : PAGE_SIZE,
    repoFilter,
    repoId,
    customAgentName,
    statuses,
    sort,
    searchQuery,
    agentIds,
    creatorIds,
    enabled: filtersReady,
    includeEnvironmentKinds,
    initialTasks: seededInitialTasks,
    initialPullRequestsMap: seededPullRequestsMap,
    scope: includeChats ? TASK_LIST_SCOPE : undefined,
    preserveHiddenTaskPositions: true,
  })
  const chatQuery = useTasks({
    taskFilter: filterActiveTab === 'closed' ? 'completed' : 'active',
    taskType: TASK_TYPE_CHAT,
    includeCounts: true,
    currentPage: 1,
    pageSize: cumulativePageSize,
    repoFilter,
    customAgentName,
    statuses,
    sort,
    searchQuery,
    agentIds,
    creatorIds,
    enabled: filtersReady && includeChats,
    scope: CHAT_LIST_SCOPE,
    preserveHiddenTaskPositions: true,
  })

  const mergedTasks = useMemo(() => {
    const seenTaskIds = new Set<string>()
    const chatTaskIds = new Set(chatQuery.tasks.flatMap(task => (task.id ? [task.id] : [])))
    const taskCandidates = [...taskQuery.tasks, ...(includeChats ? chatQuery.tasks : [])].map(task =>
      task.id && chatTaskIds.has(task.id) ? {...task, taskType: TASK_TYPE_CHAT} : task,
    )
    const sortedTasks = sortTasksByDate(
      taskCandidates.filter(task => {
        const taskId = String(task.clientGeneratedId ?? task.id ?? task.resource_id)
        if (seenTaskIds.has(taskId)) return false
        seenTaskIds.add(taskId)
        return true
      }),
    )
    return sortDirection === 'oldest' ? sortedTasks.reverse() : sortedTasks
  }, [chatQuery.tasks, includeChats, sortDirection, taskQuery.tasks])
  const pageStart = includeChats ? (currentPage - 1) * PAGE_SIZE : 0
  const pageEnd = pageStart + PAGE_SIZE
  const hiddenTaskIds = useHiddenEarlyTaskIds()
  const pageTasks = includeChats ? mergedTasks.slice(pageStart, pageEnd) : taskQuery.tasks
  const tasks = pageTasks.filter(task => !task.id || !hiddenTaskIds.has(task.id))
  const hasNextPage = includeChats
    ? pageTasks.length > 0 && (mergedTasks.length > pageEnd || taskQuery.hasNextPage || chatQuery.hasNextPage)
    : taskQuery.hasNextPage
  const isLoading = taskQuery.isLoading || (includeChats && chatQuery.isLoading)
  const isFetching = taskQuery.isFetching || (includeChats && chatQuery.isFetching)
  // Either stream failing is tolerable as long as the other one still has
  // results to show; only surface the error when nothing merged through.
  const isError = includeChats
    ? (taskQuery.isError || chatQuery.isError) && mergedTasks.length === 0
    : taskQuery.isError
  const dataUpdatedAt = Math.max(taskQuery.dataUpdatedAt, includeChats ? chatQuery.dataUpdatedAt : 0)
  const counts = useMemo(() => {
    const taskCounts = taskQuery.counts
    if (!includeChats) return taskCounts

    const chatCounts = chatQuery.counts
    if (!taskCounts && !chatCounts) return undefined

    const combinedCounts = {
      open: (taskCounts?.open ?? 0) + (chatCounts?.open ?? 0),
      closed: (taskCounts?.closed ?? 0) + (chatCounts?.closed ?? 0),
    }

    // Hydration omits inaccessible tasks (for example, tasks behind SSO). If
    // that leaves the current page empty, the visible cumulative result is the
    // only count the user can navigate, so do not advertise hidden pages.
    if (tasks.length === 0 && mergedTasks.length < pageEnd) {
      if (filterActiveTab === 'closed') {
        combinedCounts.closed = mergedTasks.length
      } else {
        combinedCounts.open = mergedTasks.length
      }
    }

    return combinedCounts
  }, [chatQuery.counts, filterActiveTab, includeChats, mergedTasks.length, pageEnd, taskQuery.counts, tasks.length])

  useEffect(() => {
    onTotalTaskCount?.(counts?.open, isNonDefault || isRepoScoped)
  }, [counts?.open, isNonDefault, isRepoScoped, onTotalTaskCount])

  const showSkeletonLoaders = isLoading || (isFetching && tasks.length === 0)

  const hpcEnabled = trackHpc && !showSkeletonLoaders && filtersReady

  const showingFilteredEmpty = tasks.length === 0 && hasActiveFilters

  const clearButton = isNonDefault ? (
    <IconButton
      aria-label="Reset filters"
      icon={XCircleFillIcon}
      variant="invisible"
      size="small"
      onClick={handleResetToDefault}
    />
  ) : null

  const hideTabs = isNeedsAttentionView
  const openCount = hideTabs && !showSkeletonLoaders ? counts?.open : undefined
  const sessionCountText = openCount != null ? formatSessionCount(openCount) : null

  return (
    <TaskActionProviders>
      <ArchiveErrorBanner />
      <div className={styles.filterBarContainer}>
        <Filter
          id="session-filter"
          label="Filter sessions"
          variant="input"
          providers={filterProviders}
          filterValue={filterValue}
          onChange={handleFilterChange}
          onSubmit={handleFilterSubmit}
          showClearButton={false}
          trailingVisual={clearButton}
        />
      </div>
      <div data-testid="agents-task-list-hpc">
        {hpcEnabled ? <span {...hpcAttrs()} data-testid="agents-task-list-hpc-marker" hidden /> : null}
        <TaskListLayout
          tasks={tasks}
          isError={isError}
          isLoading={isLoading}
          showSkeletonLoaders={showSkeletonLoaders}
          hasNextPage={hasNextPage}
          density="compact"
          header={
            <div className={styles.headerContainer}>
              {hideTabs ? (
                sessionCountText != null && <span className={styles.sessionCount}>{sessionCountText}</span>
              ) : (
                <ul className={clsx(styles.tabsContainer, 'list-style-none')} aria-label="Filter by status">
                  <li>
                    <ListViewSectionFilterLink
                      title="Active"
                      isSelected={filterActiveTab === 'open'}
                      count={counts?.open?.toLocaleString()}
                      isLoading={showSkeletonLoaders}
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        handleTabClick('open')
                      }}
                    />
                  </li>
                  <li>
                    <ListViewSectionFilterLink
                      title="Completed"
                      isSelected={filterActiveTab === 'closed'}
                      count={counts?.closed?.toLocaleString()}
                      isLoading={showSkeletonLoaders}
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        handleTabClick('closed')
                      }}
                    />
                  </li>
                </ul>
              )}
              <div className={styles.filterBar}>
                <FilterActionBar
                  activeTab={filterActiveTab}
                  owner={repoOwner}
                  repo={repoName}
                  sortDirection={sortDirection}
                  onSortChange={handleSortChange}
                  selectedStatuses={selectedStatuses}
                  onStatusChange={handleStatusChange}
                  agentOptions={agentOptions}
                  selectedAgents={selectedAgents}
                  onAgentChange={handleAgentChange}
                  selectedTypes={selectedTypes}
                  onTypeChange={handleTypeChange}
                  typeOptions={typeOptions}
                  filterButtonClassName={styles.filterButton}
                  repoFilter={repoFilter}
                  onRepoFilterChange={setRepoFilter}
                />
              </div>
            </div>
          }
          emptyHeading={showingFilteredEmpty ? 'No sessions match your filters' : 'No agent sessions'}
          emptyDescription={
            showingFilteredEmpty ? 'Try adjusting your search or filters.' : 'Start a new session to see it here.'
          }
          errorText="Unable to load tasks, try again later."
          renderItem={task => (
            <TaskListItem
              key={task.id ?? task.clientGeneratedId}
              task={task}
              nwo={task.nwo}
              getTaskUrl={getTaskUrl}
              currentUserId={currentUserId}
              dataUpdatedAt={dataUpdatedAt}
              analyticsTarget="AGENTS_PAGE_TASK"
              renderDescription={(t, props) => <TaskDescription {...props} parts={buildDescriptionParts(t)} />}
              suppressAttentionStyling={isNeedsAttentionView}
            />
          )}
        />
      </div>
    </TaskActionProviders>
  )
}

function buildDescriptionParts(task: UITask): ReactNode[] {
  if (task.isOptimistic) {
    return [task.nwo ? <span key="repo">{task.nwo}</span> : null, <span key="init">Initializing session...</span>]
  }

  const pullText = task.pull?.number ? `#${task.pull.number}` : null
  const repoAndPull = [task.nwo, pullText].filter(Boolean).join(' ')

  return [
    repoAndPull ? <span key="repo">{repoAndPull}</span> : null,
    <span key="time">
      <TaskRelativeTime task={task} />
    </span>,
    task.session_count > 1 ? <span key="revisions">{task.session_count} revisions</span> : null,
    <span key="state">{getSessionStateText(task)}</span>,
  ]
}
